'use client';

import { LoaderPlaceholder } from '@/components/Placeholder';
import { getClassNames } from '@/utils/helpers';
import {
  Banner,
  EmptyState,
  IconSticker,
  Table,
  Title,
  Status,
  Button,
} from '@yaradigitallabs/ahua-react';
import type React from 'react';
import type { ProductionPeriod } from '../../utils/types';
import { formatDuration, formatPeriodRange } from '../../utils/helpers';
import { ReportStatus, ReportStatusLabel } from '../../enums';

type AddProductionReportingListTableProps = {
  list: ProductionPeriod[];
  isLoading: boolean;
  error?: string | null;
  isDeleting?: boolean;
  onEditPeriod: (id: string) => void;
  onDeletePeriod: (id: string) => void;
  onReportPeriod: (pp: ProductionPeriod) => void;
};

const AddProductionReportingListTable: React.FC<
  AddProductionReportingListTableProps
> = ({
  list,
  isLoading,
  error,
  onEditPeriod,
  onDeletePeriod,
  onReportPeriod,
}) => {
  return (
    <div className="border border-gray-300 rounded-lg bg-white flex-1">
      <div className="flex items-center px-8 py-4 border-b border-gray-300">
        <Title maxLineCount={1} size="s">
          List of added periods
        </Title>
      </div>
      <div className="px-4 h-[calc(100vh-420px)] md:h-[calc(100vh-420px)] overflow-auto">
        <Table
          className={getClassNames(
            'w-full relative',
            !isLoading && list.length === 0 ? 'h-full' : ''
          )}
        >
          <thead className="sticky top-0 bg-white z-10 shadow-sm">
            <Table.Row>
              <Table.Head className="py-5! px-3! text-nowrap">
                Period
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Duration
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Material number
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Material description
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Production order
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Status
              </Table.Head>
              <Table.Head className="py-5! px-3!"></Table.Head>
            </Table.Row>
          </thead>
          <tbody>
            {!isLoading &&
              !error &&
              list.map(period => (
                <Table.Row key={period.id}>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    {formatPeriodRange(
                      period.start_time,
                      period.end_time,
                      false
                    )}
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    {formatDuration(period.start_time, period.end_time)}
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    {period.material_code}
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    {period.material_description}
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    {period.production_order}
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3! text-nowrap">
                    <StatusLabel status={period.status} />
                  </Table.Cell>
                  <Table.Cell className="py-3! px-3!">
                    <div className="flex gap-3 justify-end">
                      {[ReportStatus.REPORT_PENDING].includes(
                        period.status
                      ) && (
                        <>
                          <Button
                            colorConcept="destructive"
                            iconLeading="Delete"
                            size="xs"
                            variant="ghost"
                            onClick={() => onDeletePeriod(period.id)}
                          >
                            Delete
                          </Button>
                          <Button
                            iconLeading="Edit"
                            colorConcept="brand"
                            size="xs"
                            variant="ghost"
                            onClick={() => onEditPeriod(period.id)}
                          >
                            Edit
                          </Button>
                        </>
                      )}
                      <Button
                        iconTrailing="ChevronRight"
                        colorConcept="brand"
                        size="xs"
                        variant="ghost"
                        onClick={() => onReportPeriod(period)}
                      >
                        {period.status === ReportStatus.REPORT_PENDING
                          ? 'Report'
                          : 'Detail'}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            {isLoading && (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Table.Row className="border-b-0!" key={`loader_${index}`}>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                    <Table.Cell className="py-5! px-3! text-nowrap">
                      <LoaderPlaceholder />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </>
            )}

            {error && (
              <Table.Row className="border-b-0!">
                <Table.Cell className="text-center py-6" colSpan={6}>
                  <Banner colorConcept="destructiveLight" open>
                    <Banner.Middle>
                      <Banner.Body>
                        <Banner.Message size="n">
                          An error occurred while fetching production records.
                        </Banner.Message>
                      </Banner.Body>
                    </Banner.Middle>
                  </Banner>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading && !error && list.length === 0 && (
              <Table.Row className="border-b-0!">
                <Table.Cell className="text-center py-6" colSpan={6}>
                  <EmptyState
                    captionText=""
                    iconStickerProps={{
                      icon: 'Info',
                      size: 'xl',
                    }}
                    className="w-xs lg:max-w-sm! mx-auto"
                    titleText="No period added yet"
                  >
                    <EmptyState.Start>
                      <IconSticker icon="Info" size="xl" />
                    </EmptyState.Start>
                    <EmptyState.Middle>
                      <EmptyState.Title>No period added yet</EmptyState.Title>
                      <EmptyState.Caption>
                        Click the button "Add production period" on the top
                        right to start adding the periods.
                      </EmptyState.Caption>
                    </EmptyState.Middle>
                  </EmptyState>
                </Table.Cell>
              </Table.Row>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const StatusLabel: React.FC<{ status: ReportStatus }> = ({ status }) => {
  const isReported = status !== ReportStatus.REPORT_PENDING;
  return (
    <div className="flex justify-start items-center">
      <Status
        colorType={isReported ? 'green' : 'gray'}
        iconLeading={isReported ? 'Check' : 'Skeleton'}
        size="n"
      >
        {isReported
          ? ReportStatusLabel[ReportStatus.REPORTED]
          : ReportStatusLabel[status]}
      </Status>
    </div>
  );
};

export default AddProductionReportingListTable;
